import CollapsibleCard from "./CollapsibleCard";
import { useFundingRequestForm } from "./FundingRequestFormContext";
import { formatNumber, formatAccounting } from "helpers/formatNumber";
import { formatPhoneNumber } from "helpers/formatPhoneNumber";
import dayjs from "dayjs";
import { FR_CONTRACT_TYPE, CARRIER_FR_CONTRACT_TYPE } from "constants/haulpay-broker";
import clsx from "clsx";
import Toggle from "components/Toggle";
import { useFormContext, useWatch } from "react-hook-form";
import { IFundingRequest } from "models/HaulPay";
import { useSelector } from "react-redux";
import { selectFactoringProfile } from "store/user/selector";
import NOAStatus from "../../../../../../components/NOAStatus";
import DebtorVerificationStatus from "../../../../../../components/DebtorVerificationStatus";
import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { EQUIPMENT_TYPES } from "../../../../../../constants/signup";
import { getInvoiceForFRAPI } from "services/api/haulpay";
import { showErrorMessage } from "helpers/toastMessage";
import Icon from "@mdi/react";
import { mdiLoading } from "@mdi/js";
import { useUserFeature } from "hooks/useUserFeature";
import { useBrokerContext } from "context/BrokerContext";
import Tooltip from "components/Tooltip";
import ControlledTooltip from "./ControlledTooltip";

type Address = {
  city?: string;
  state?: string;
  country?: string;
  [key: string]: any; // For other address fields we don't use
};

const formatAddress = (address?: Address): string => {
  if (!address) return "—";
  const { street_one, city, state, country } = address;
  return [street_one, city, state, country].filter(Boolean).join(", ") || "—";
};

function Row({
  label,
  value,
  colorClass,
  labelClass
}: {
  label: string | React.ReactNode;
  value?: React.ReactNode;
  colorClass?: string;
  labelClass?: string;
}) {
  return (
    <div className="flex items-start gap-2 py-1">
      <span className={clsx(labelClass, "text-tertiary text-sm basis-1/2 shrink-0")}>{label}</span>
      <span className={clsx(colorClass, "text-sm grow min-w-0 text-right break-words")}>
        {value ?? "—"}
      </span>
    </div>
  );
}

type StatusResponse = {
  status: string;
  className: string;
};

const getStatus = (status: string): StatusResponse => {
  switch (status) {
    case "Pending":
    case "pending":
      return { status: "Pending", className: "text-yellow-500" };
    case "Incomplete":
    case "incomplete":
      return { status: "Incomplete", className: "text-red-600" };
    case "Approved":
    case "approved":
      return { status: "Approved", className: "text-green-500" };
    case "Document Issue":
    case "document_issue":
      return { status: "Doc Issue", className: "text-red-600" };
    case "Declined":
    case "declined":
    case "declined_3_months":
    case "declined_6_months":
      return { status: "Declined", className: "text-red-600" };
    case "do_not_contact":
      return { status: "Do Not Contact", className: "text-blue-500" };
    case "Paid":
    case "paid":
      return { status: "Paid", className: "text-green-600" };
    case "needs_review":
      return { status: "Needs Review", className: "text-yellow-500" };
    default:
      return { status: "Pending", className: "text-yellow-500" };
  }
};

const getCarrierStatus = (status?: string): StatusResponse => {
  switch (status?.toLowerCase()) {
    case "approved":
      return { status: "Approved", className: "text-green-500" };
    case "pending":
      return { status: "Pending", className: "text-yellow-500" };
    case "declined":
      return { status: "Declined", className: "text-red-600" };
    case "needs_review":
      return { status: "Needs Review", className: "text-blue-500" };
    default:
      return { status: "—", className: "text-tertiary" };
  }
};

const formatReceivableStatus = (status?: string): string | undefined =>
  status
    ? status
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : undefined;

type RowConfig = {
  label: string | React.ReactNode;
  value?: React.ReactNode;
  colorClass?: string;
  labelClass?: string;
};

export default function LeftSummary() {
  const { isCarrier } = useUserFeature();
  const {
    selectedFR,
    loading,
    brokerGrossMargin,
    reserveHoldBack,
    factoringPayable,
    brokersQPFee,
    carrierRate,
    carrierFee,
    yourFee,
    carrierPay,
    selectedPaymentProfile,
    setIsAttachmentsListIsModalOpen,
    selfFinanceFee,
    selfFinanceFeeRateInfo,
    brokerCarrierAdvance,
    selfFinanceTotalAmount,
    brokerQPProfit,
    isAssignedLoadsPage,
    factoringFee,
    isFetchingCalculation,
    debtorAdvanceAmount,
    debtorAdditionalDiscountRate,
    brokerAdditionalDiscountFeeForCarrier,
    brokerAdditionalDiscountFeeForBroker,
    marginAdvanceFee,
    marginAdvanceAmount,
    brokerAdvanceFeePercent,
    brokerDiscountFeeForCarrier,
    isOtherFieldsDisabled,
    otherFieldsDisabledTooltip
  } = useFundingRequestForm();
  const margin = selectedFR?.purchases?.find((purchase: any) => purchase?.category === "margin");
  const deliveryAdvance = selectedFR?.purchases?.find(
    (purchase: any) => purchase?.category === "delivery_advance_broker"
  );
  const carrierAmountFunded = (selectedFR?.purchases ?? [])
    .filter((purchase: any) => purchase?.category === "delivery_advance")
    .reduce((sum: number, purchase: any) => sum + (purchase?.amount || 0), 0);
  const isSelfFinance = selectedFR?.contractType?.startsWith?.("SELF_FINANCE");
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  const handleViewInvoice = useCallback(async () => {
    if (!selectedFR?.id || isLoadingInvoice) return;

    setIsLoadingInvoice(true);
    try {
      const { data } = await getInvoiceForFRAPI(selectedFR.id, "invoice_payment_profile");
      if (data?.preview_url) {
        window.open(data.preview_url, "_blank");
      }
    } catch (error) {
      showErrorMessage("Failed to load invoice. Please try again.");
    } finally {
      setIsLoadingInvoice(false);
    }
  }, [selectedFR?.id, isLoadingInvoice]);

  // Format currency amounts
  const formatCurrency = (cents?: number) => {
    if (cents === undefined || cents === null || isNaN(cents)) return "$0.00";
    return `${formatNumber(cents / 100, 2)}`;
  };

  // Format debit amounts with accounting format (parentheses for negative)
  const formatDebit = (cents?: number) => {
    if (cents === undefined || cents === null || isNaN(cents)) return "($0.00)";
    return formatAccounting(-Math.abs(cents) / 100, 2);
  };

  // Format credit amounts with accounting format
  const formatCredit = (cents?: number) => {
    if (cents === undefined || cents === null || isNaN(cents)) return "$0.00";
    return formatAccounting(Math.abs(cents) / 100, 2);
  };

  // Amounts that come from the rate calculation rather than from the funding
  // request itself. Until the calculation resolves these are unknown, not zero,
  // so they must not render as $0.00.
  const isUnknownAmount = (cents?: number) => cents === undefined || cents === null || isNaN(cents);
  const formatCalculated = (cents?: number) =>
    isUnknownAmount(cents) ? "-" : formatCurrency(cents);
  const formatCalculatedDebit = (cents?: number) =>
    isUnknownAmount(cents) ? "-" : formatDebit(cents);

  const { register, control } = useFormContext<Partial<IFundingRequest>>();

  const factoringProfile = useSelector(selectFactoringProfile);
  const contractType = useWatch({ control: control, name: "fundingRequestType" });
  const shipperInvoiceAmount = useWatch({ control: control, name: "shipperInvoiceAmount" });
  const carrierInvoiceAmount = useWatch({ control: control, name: "carrierInvoiceAmount" });
  const marginAdvance = useWatch({ control: control, name: "marginAdvance" });
  const isNonFactored =
    contractType?.value === "NON_FACTORED_BILL_OUT" ||
    contractType?.value === "NON_FACTORED_STANDARD";
  const marginPaymentPurchase = isNonFactored ? deliveryAdvance : margin;
  const carrierPaymentSpeed = useWatch({ control: control, name: "carrierPaymentSpeed" });
  const carrierPaymentMethod = useWatch({ control: control, name: "carrierPaymentMethod" });

  // Get payment speed label from BrokerContext (uses fetched settings)
  const { getPaymentSpeedLabel, defaultPaymentSettings } = useBrokerContext();

  // Get the carrier payment speed label - updates when defaultPaymentSettings loads
  const resolvedCarrierSpeedLabel = useMemo(() => {
    return getPaymentSpeedLabel(carrierPaymentSpeed);
  }, [carrierPaymentSpeed, getPaymentSpeedLabel, defaultPaymentSettings]);

  // Preserve the last stable payment speed label while calculation is in-flight
  const prevCarrierSpeedLabelRef = useRef<string | undefined>(resolvedCarrierSpeedLabel);
  useEffect(() => {
    // Only update the ref when calculation finishes AND we have a valid resolved label
    if (!isFetchingCalculation && resolvedCarrierSpeedLabel) {
      prevCarrierSpeedLabelRef.current = resolvedCarrierSpeedLabel;
    }
  }, [isFetchingCalculation, resolvedCarrierSpeedLabel]);

  // Invoice/Load Info rows
  const invoiceInfoRows: RowConfig[] = isCarrier
    ? [
        { label: "Invoice #", value: selectedFR?.invoiceNumber },
        {
          label: "Invoice Date",
          value: selectedFR?.invoiceDate ? dayjs(selectedFR?.invoiceDate).format("MM/DD/YYYY") : "-"
        },
        {
          label: "Your Invoice Number",
          value: selectedFR?.clientInvoiceNumber || selectedFR?.userReferenceNumber
        },
        { label: "Load #", value: selectedFR?.userLoadNumber },
        {
          label: "Funding Request Status",
          value: selectedFR?.status,
          colorClass: getStatus(selectedFR?.status)?.className
        },
        // HP-9254: the note explains a Document Issue, so it belongs beside the status rather than
        // behind the collapse. HP-9215 still applies: on an assigned load the carrier is looking at
        // a broker request, so it stays withheld there.
        ...(isAssignedLoadsPage ? [] : [{ label: "Status Note", value: selectedFR?.statusNote }])
      ]
    : [
        { label: "Invoice #", value: selectedFR?.invoiceNumber },
        { label: "Load #", value: selectedFR?.userLoadNumber },
        { label: "PO#", value: selectedFR?.purchaseOrderNumber },
        {
          label: "Funding Request Status",
          value: selectedFR?.status,
          colorClass: getStatus(selectedFR?.status)?.className
        }
      ];

  const invoiceInfoCollapsibleRows: RowConfig[] = isCarrier
    ? [
        {
          label: "Create Date",
          value: selectedFR?.created
            ? dayjs(selectedFR.created).format("MM/DD/YYYY")
            : dayjs().format("MM/DD/YYYY")
        },
        { label: "Current Status Date", value: dayjs().format("MM/DD/YYYY") },
        { label: "Bill To Invoice Details", value: selectedFR?.userNotes },
        { label: "First Pick up", value: formatAddress(selectedFR?.firstOriginLocation) },
        { label: "Final Delivery", value: formatAddress(selectedFR?.finalDestinationLocation) },
        { label: "Load Length", value: selectedFR?.loadLength },
        {
          label: "Equipment Type",
          value:
            EQUIPMENT_TYPES.find((item) => item.value === selectedFR?.LoadTrailerType)?.label ||
            EQUIPMENT_TYPES[0]?.label
        },
        {
          label: "Attachments List",
          value: selectedFR?.attachments?.length ? (
            <a onClick={() => setIsAttachmentsListIsModalOpen(true)}>View All</a>
          ) : (
            "No attachments"
          ),
          colorClass: "text-orange-500"
        }
      ]
    : [
        { label: "Client Invoice #", value: selectedFR?.clientInvoiceNumber },
        { label: "Status Note", value: selectedFR?.statusNote },
        {
          label: "Create Date",
          value: selectedFR?.created
            ? dayjs(selectedFR.created).format("MM/DD/YYYY")
            : dayjs().format("MM/DD/YYYY")
        },
        { label: "Current Status Date", value: dayjs().format("MM/DD/YYYY") },
        { label: "Shipper/Debtor Invoice Details", value: selectedFR?.debtorInvoiceNotes },
        { label: "Internal Funding Notes", value: selectedFR?.userNotes },
        { label: "First Pick up", value: formatAddress(selectedFR?.firstOriginLocation) },
        { label: "Final Delivery", value: formatAddress(selectedFR?.finalDestinationLocation) },
        { label: "Load Length", value: selectedFR?.loadLength },
        {
          label: "Equipment Type",
          value:
            EQUIPMENT_TYPES.find((item) => item.value === selectedFR?.LoadTrailerType)?.label ||
            EQUIPMENT_TYPES[0]?.label
        },
        {
          label: "Attachments List",
          value: selectedFR?.attachments?.length ? (
            <a onClick={() => setIsAttachmentsListIsModalOpen(true)}>View All</a>
          ) : (
            "No attachments"
          ),
          colorClass: "text-orange-500"
        },
        {
          label: "Receivable Status",
          value: formatReceivableStatus(selectedFR?.receivableStatus)
        },
        {
          label: "Receivable Paid Date",
          value: selectedFR?.receivablePaidDate
            ? dayjs(selectedFR.receivablePaidDate).format("MM/DD/YYYY")
            : undefined
        },
        {
          label: "Receivable Paid Amount",
          value:
            selectedFR?.receivablePaidAmount != null
              ? formatNumber(selectedFR.receivablePaidAmount, 2)
              : undefined
        }
      ];

  const debtorAssignedLoadRows = (): RowConfig[] => {
    const companyProfile = JSON.parse(selectedFR?.factoring?.companyProfile ?? "{}");
    const ownerProfile = JSON.parse(selectedFR?.factoring?.ownerProfile ?? "{}");
    return [
      {
        label: "Bill To",
        value: companyProfile?.dba || companyProfile.name
      },
      { label: "Bill To Company Address", value: formatAddress(companyProfile?.address) },
      {
        label: "Bill To Company Phone",
        value: ownerProfile?.phone_number_cell
          ? formatPhoneNumber(ownerProfile.phone_number_cell)
          : "—"
      },
      { label: "Shipper/Debtor Email", value: selectedFR?.factoring?.ccBrokerRateConfirmationEmail }
    ];
  };

  // Debtor Details rows
  const debtorDetailsRows: RowConfig[] = isCarrier
    ? [
        {
          label: "Bill To",
          value: selectedFR?.debtor?.dba || selectedFR?.debtor?.name
        },
        { label: "Bill To Company Address", value: formatAddress(selectedFR?.billToAddress) },
        {
          label: "Bill To Company Phone",
          value: selectedFR?.billToCompanyPhone
            ? formatPhoneNumber(selectedFR.billToCompanyPhone)
            : "—"
        },
        { label: "Shipper/Debtor Email", value: selectedFR?.billToCompanyEmail },
        {
          label: "Credit Approved",
          value: getStatus(selectedFR?.debtor?.creditApproved)?.status,
          colorClass: getStatus(selectedFR?.debtor?.creditApproved)?.className
        },
        {
          label: "NOA STATUS",
          value: (
            <NOAStatus
              {...selectedFR?.factoringDebtorRelation}
              debtorId={selectedFR?.debtor?.id}
              factoringId={selectedFR?.factoringId}
            />
          )
        },
        {
          label: "Current Available Credit",
          value: formatCurrency(selectedFR?.debtor?.creditAvailable || 0)
        }
      ]
    : [
        {
          label: "Shipper/Debtor",
          value: selectedFR?.debtor?.dba || selectedFR?.debtor?.name
        },
        {
          label: "Shipper Invoice Amnt.",
          value: formatCurrency((shipperInvoiceAmount || 0) * 100)
        },
        { label: "Shipper/Debtor Email", value: selectedFR?.debtor?.email },
        {
          label: "Credit Approved",
          value: getStatus(selectedFR?.debtor?.creditApproved)?.status,
          colorClass: getStatus(selectedFR?.debtor?.creditApproved)?.className
        },
        {
          label: "NOA STATUS",
          value: (
            <NOAStatus
              {...selectedFR?.factoringDebtorRelation}
              debtorId={selectedFR?.debtor?.id}
              factoringId={selectedFR?.factoringId}
            />
          )
        },
        ...(selectedFR?.debtor?.profileVerified
          ? [
              {
                label: "VERIFICATION STATUS",
                value: (
                  <DebtorVerificationStatus profileVerified={selectedFR?.debtor?.profileVerified} />
                )
              }
            ]
          : []),
        {
          label: "Current Available Credit",
          value: formatCurrency(selectedFR?.debtor?.creditAvailable || 0)
        }
      ];

  const debtorDetailsCollapsibleRows: RowConfig[] = isCarrier
    ? [{ label: "Bill To Invoice Details", value: selectedFR?.debtorInvoiceNotes }]
    : [
        {
          label: "Shipper/Debtor Phone",
          value: selectedFR?.debtor?.phoneNumber
            ? formatPhoneNumber(selectedFR.debtor.phoneNumber)
            : "—"
        },
        { label: "Shipper/Debtor Invoice Details", value: selectedFR?.debtorInvoiceNotes }
      ];

  // Carrier Details rows
  const carrierStatus = getCarrierStatus(selectedFR?.paymentProfile?.status);
  const carrierDetailsRows: RowConfig[] = [
    {
      label: "Carrier",
      value: selectedPaymentProfile?.company_name || selectedFR?.paymentProfile?.companyName
    },
    {
      label: "Carrier MC",
      value: selectedPaymentProfile?.company_mc || selectedFR?.paymentProfile?.companyMc
    },
    {
      label: "Carrier DOT",
      value: selectedPaymentProfile?.company_dot || selectedFR?.paymentProfile?.companyDot
    },
    {
      label: "Carrier Status",
      value: carrierStatus.status,
      colorClass: carrierStatus.className
    },
    {
      label: "Carrier Invoice Amnt.",
      value: formatCurrency(
        ((carrierInvoiceAmount ?? selectedFR?.paymentProfileAmount ?? 0) as number) * 100
      )
    },
    { label: "Carrier Pro #", value: selectedFR?.carrierProNumber }
  ];

  const carrierDetailsCollapsibleRows: RowConfig[] = [
    { label: "Carrier Rate", value: carrierRate },
    { label: "Carrier Fee", value: formatDebit(carrierFee) },
    { label: "Carrier Pay", value: formatCredit(carrierPay || 0) },
    {
      label: "Carrier Expected Processing At",
      value:
        selectedFR?.scheduledPurchaseDate &&
        dayjs(selectedFR.scheduledPurchaseDate).format("MM/DD/YYYY")
    },
    {
      label: "Carrier Funded At",
      value: selectedFR?.fundedAt && dayjs(selectedFR.fundedAt).format("MM/DD/YYYY")
    },
    {
      label: "Carrier Amount Funded",
      value: formatCurrency(carrierAmountFunded)
    },
    { label: "Carrier Invoice Details", value: selectedFR?.carrierInvoiceNotes },
    {
      label: "View Carrier Invoice",
      value: (
        <span className="flex justify-end items-center gap-1">
          <a
            href="#"
            className="text-primary hover:underline"
            onClick={(e) => {
              e.preventDefault();
              handleViewInvoice();
            }}>
            View Invoice
          </a>
          {isLoadingInvoice && (
            <Icon path={mdiLoading} size={0.8} className="animate-spin text-primary" />
          )}
        </span>
      )
    },
    {
      label: "Carrier Email",
      value: selectedPaymentProfile?.owner_email || selectedFR?.paymentProfile?.ownerEmail
    },
    {
      label: "Carrier Phone",
      value: selectedPaymentProfile?.owner_phone_number_cell
        ? formatPhoneNumber(selectedPaymentProfile.owner_phone_number_cell)
        : selectedFR?.paymentProfile?.companyPhoneNumber
          ? formatPhoneNumber(selectedFR.paymentProfile.companyPhoneNumber)
          : "—"
    },
    {
      label: "Carrier Remittance Emails",
      value: selectedFR?.paymentProfile?.remittanceEmail
    },
    {
      label: "Carrier registered FMCSA Email",
      value: selectedFR?.paymentProfile?.fmcsaEmail
    },
    {
      label: "Carrier Payment Speed",
      value: isFetchingCalculation ? prevCarrierSpeedLabelRef.current : resolvedCarrierSpeedLabel
    },
    {
      label: "Carrier Payment Method",
      value: carrierPaymentMethod || selectedFR?.paymentProfilePaymentMethod || "—"
    }
  ];

  // Check if debtor has additional discount rate
  const hasDebtorAdditionalRate = debtorAdditionalDiscountRate > 0;

  // Broker/Carrier Pay rows
  const brokerPayRows: RowConfig[] = isCarrier
    ? [
        {
          label: "Funded At",
          value: selectedFR?.fundedAt ? dayjs(selectedFR.fundedAt).format("MM/DD/YYYY") : "-"
        },
        {
          label: "Carrier Amount Funded",
          value: formatCurrency((selectedFR?.amountFunded || 0) * 100)
        },
        {
          label: "Total Pay",
          value: formatCalculated(factoringPayable),
          colorClass: factoringPayable < 0 ? "text-red-500" : "text-green-500"
        },
        { label: "Debtor Advance", value: formatCurrency(debtorAdvanceAmount || 0) },
        ...(factoringProfile?.minimum_fee !== factoringFee
          ? [
              {
                label: "Discount Rate",
                value: selectedFR?.discountRate ? `${selectedFR.discountRate || 0}%` : "0.00%"
              }
            ]
          : []),
        {
          label: "Discount Fee",
          value: formatCalculatedDebit(factoringFee)
        },
        { label: "Fuel Advance", value: formatCurrency(selectedFR?.fuelAdvanceAmount || 0) },
        {
          label: "Fuel Advance Fee",
          value:
            selectedFR?.fuelAdvanceAmount && selectedFR?.fuelAdvanceFee > 0
              ? formatDebit(selectedFR?.fuelAdvanceFee || 0)
              : "($0.00)"
        }
      ]
    : [
        {
          label: "Broker Gross Margin",
          value:
            contractType?.value === "SELF_FINANCE_NO_INVOICE"
              ? "$0.00"
              : formatCurrency(brokerGrossMargin)
        },
        ...(marginAdvance
          ? [
              {
                label: "Reserve Holdback",
                value: reserveHoldBack > 0 ? formatDebit(reserveHoldBack) : "($0.00)"
              }
            ]
          : []),
        {
          label: "Create with Margin Advance",
          value: marginAdvance ? "Yes" : "No"
        },
        ...(marginAdvance
          ? [
              {
                label: "Margin Advance",
                value: formatCurrency(marginAdvanceAmount)
              },
              {
                label: "Margin Advance Fee",
                value: formatDebit(marginAdvanceFee)
              },
              ...(hasDebtorAdditionalRate
                ? [
                    {
                      label: "Additional Debtor Margin Advance Fee",
                      value: formatDebit(brokerAdditionalDiscountFeeForBroker)
                    }
                  ]
                : [])
            ]
          : []),
        {
          label: "Carrier Payment Fee Paid by Broker",
          value: formatDebit(brokerDiscountFeeForCarrier)
        },
        ...(hasDebtorAdditionalRate
          ? [
              {
                label: "Additional Debtor Carrier Payment Fee Paid by Broker",
                value: formatDebit(brokerAdditionalDiscountFeeForCarrier)
              }
            ]
          : []),
        {
          label: (
            <Tooltip
              message="Total fees deducted from your payment"
              position="right"
              tooltipContainerClassName="relative inline-flex"
              wrapperClass="max-w-xs !whitespace-normal">
              <span className="flex items-center gap-1 cursor-help">
                Your Fee
                <span className="text-gray-400 text-xs">ⓘ</span>
              </span>
            </Tooltip>
          ),
          value: !isSelfFinance
            ? formatDebit(yourFee)
            : `${
                selfFinanceFeeRateInfo?.includes("%")
                  ? formatDebit(selfFinanceFee || 0)
                      .replace(/^\(/i, "")
                      .replace(/\)$/i, "")
                  : formatDebit(selfFinanceFee || 0)
              } (${selfFinanceFeeRateInfo ?? "Flat"})`
        },
        {
          label: "Total Broker Pay",
          value:
            contractType?.value === "SELF_FINANCE_NO_INVOICE"
              ? "$0.00"
              : formatCredit(factoringPayable),
          colorClass: "text-green-500"
        }
      ];

  const assignedLoadRows: RowConfig[] = [
    {
      label: "Funded At",
      value: selectedFR?.fundedAt ? dayjs(selectedFR.fundedAt).format("MM/DD/YYYY") : "-"
    },
    {
      label: "Carrier Amount Funded",
      value: formatCurrency((selectedFR?.paymentProfileFundedAmount || 0) * 100)
    },
    {
      label: "Total Pay",
      value: formatCredit(carrierPay || 0),
      colorClass: "text-green-500"
    },
    {
      label: "Discount Rate",
      value: selectedFR?.displayDiscountRate ? `${selectedFR.displayDiscountRate || 0}%` : "0.00%"
    },
    {
      label: "Discount Fee",
      value: formatDebit(carrierFee)
    }
  ];

  const yourPayValue = (): string => {
    if (contractType?.value === "SELF_FINANCE_NO_INVOICE") {
      return "$0.00";
    }
    if (contractType?.value === "SELF_FINANCE_WITH_INVOICE") {
      return formatCurrency((shipperInvoiceAmount || 0) * 100);
    }
    const basePay = isAssignedLoadsPage ? carrierPay : factoringPayable;
    return formatCalculated(basePay);
  };

  const brokerPayCollapsibleRows: RowConfig[] = isCarrier
    ? []
    : [
        ...(!isNonFactored ? [{ label: "Broker's % of Carrier QP Fee", value: brokersQPFee }] : []),
        ...(Number(selectedFR?.additionalDiscountRate) > 0
          ? [
              {
                label: "Additional Debtor % on Carrier Payment",
                value: `+${selectedFR?.additionalDiscountRate}%`
              }
            ]
          : []),
        ...(marginAdvance
          ? [
              {
                label: "Broker's Advance Fee %",
                value: `${brokerAdvanceFeePercent * 100 || 0}%`
              }
            ]
          : []),
        ...(Number(selectedFR?.additionalDiscountRate) > 0
          ? [
              {
                label: "Additional Debtor % on Broker Advance",
                value: `+${selectedFR?.additionalDiscountRate}%`
              }
            ]
          : []),
        ...(marginAdvance
          ? [
              {
                label: "Brokers Advance Payment Amount",
                value: formatCurrency(deliveryAdvance?.amount || 0)
              },
              {
                label: "Brokers Advance Payment date",
                value: deliveryAdvance?.postingDate
                  ? dayjs(deliveryAdvance?.postingDate).format("MM/DD/YYYY")
                  : "N/A"
              }
            ]
          : []),
        {
          label: "Brokers Margin Payment Amount",
          value: formatCurrency(marginPaymentPurchase?.amount || 0)
        },
        {
          label: "Brokers Margin Payment date",
          value: marginPaymentPurchase?.postingDate
            ? dayjs(marginPaymentPurchase?.postingDate).format("MM/DD/YYYY")
            : "N/A"
        }
      ];

  const highlightInfoRows: RowConfig[] = [
    {
      label: "",
      value: selectedFR?.status,
      colorClass: `${getStatus(selectedFR?.status)?.className} font-semibold !text-lg`,
      labelClass: "font-semibold"
    },
    {
      label: "",
      value: (isCarrier ? CARRIER_FR_CONTRACT_TYPE : FR_CONTRACT_TYPE).find(
        ({ value }) => selectedFR?.contractType === value
      )?.label,
      colorClass: "font-semibold"
    },
    {
      label: "Inv. amnt.",
      value: formatCurrency((shipperInvoiceAmount || 0) * 100),
      labelClass: "font-semibold",
      colorClass: "font-semibold"
    },
    {
      label: "Your Pay",
      value: yourPayValue(),
      colorClass:
        (isCarrier
          ? isAssignedLoadsPage
            ? carrierPay || 0
            : factoringPayable || 0
          : factoringPayable || 0) < 0
          ? "text-red-500 font-semibold"
          : "text-green-500 font-semibold",
      labelClass: "font-semibold"
    }
  ];

  const selfFinanceInfoRows: RowConfig[] = [
    {
      label: "LineHaul",
      value: formatCurrency(brokerCarrierAdvance || 0),
      colorClass: "text-red-500"
    },
    {
      label: "Self Finance Fee",
      value: `${
        selfFinanceFeeRateInfo?.includes("%")
          ? formatCurrency(selfFinanceFee || 0).replace(/^\$/i, "")
          : formatCurrency(selfFinanceFee || 0)
      } (${selfFinanceFeeRateInfo ?? "Flat"})`,
      colorClass: "text-red-500"
    },
    {
      label: "Gross QP Profit",
      value: formatCurrency(brokerQPProfit || 0),
      colorClass: "text-green-500"
    },
    {
      label: "Debit Amount",
      value: formatCurrency(selfFinanceTotalAmount || 0),
      colorClass: "text-red-500"
    },
    {
      label: "Net QP Profit",
      value: formatCurrency((brokerQPProfit || 0) - (selfFinanceFee || 0)),
      colorClass: "text-green-500"
    }
  ];

  const renderRows = (rows: RowConfig[]) => (
    <>
      {rows.map((row, index) => (
        <Row key={`${row.label}-${index}`} {...row} />
      ))}
    </>
  );

  return (
    <aside className="flex flex-col gap-4">
      {/* HP-9215: margin advance is a broker financing decision. The rows below already sit in the
          broker branch of brokerPayRows; the toggle had no carrier gate at all, so an invited
          carrier saw it on any factored funding request assigned to them. */}
      {!isCarrier &&
        factoringProfile?.enable_reserve_holdback_hybrid &&
        !contractType?.value.startsWith("NON_FACTORED") &&
        !contractType?.value.startsWith("SELF_FINANCE") &&
        (hasDebtorAdditionalRate ? (
          <ControlledTooltip
            id="margin-advance-toggle-with-rate"
            message={
              isOtherFieldsDisabled
                ? otherFieldsDisabledTooltip
                : `If you choose to take a margin advance, an additional ${debtorAdditionalDiscountRate * 100}% discount will apply due to this debtor's extended payment terms.`
            }
            position="down"
            tooltipContainerClassName="w-full"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={false}>
            <div className="bg-card shadow-lg rounded-lg border-card flex items-center justify-between px-2 py-4 gap-1 cursor-help">
              <h6 className="text-sm font-medium flex items-center gap-1">
                Create w Margin Advance
                <span className="text-gray-400 text-xs">ⓘ</span>
              </h6>
              <Toggle
                className="items-center self-end"
                name="marginAdvance"
                register={register}
                disabled={contractType?.value.startsWith("SELF_FINANCE") || isOtherFieldsDisabled}
              />
            </div>
          </ControlledTooltip>
        ) : (
          <ControlledTooltip
            id="margin-advance-toggle"
            message={otherFieldsDisabledTooltip}
            position="down"
            tooltipContainerClassName="w-full"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled}>
            <div className="bg-card shadow-lg rounded-lg border-card flex items-center justify-between px-2 py-4 gap-1">
              <h6 className="text-sm font-medium">Create w Margin Advance</h6>
              <Toggle
                className="items-center self-end"
                name="marginAdvance"
                register={register}
                disabled={contractType?.value.startsWith("SELF_FINANCE") || isOtherFieldsDisabled}
              />
            </div>
          </ControlledTooltip>
        ))}
      <CollapsibleCard
        id="highlight-info"
        title="Highlights"
        defaultOpen
        displayArrow={false}
        titleClassName="!text-lg !font-semibold">
        {loading ? (
          <div className="animate-pulse flex flex-col gap-4">
            <div className="h-5 bg-foreground rounded-md"></div>
            <div className="h-5 bg-foreground rounded-md"></div>
          </div>
        ) : (
          <div className="divide-y divide-card/50">{renderRows(highlightInfoRows)}</div>
        )}
      </CollapsibleCard>
      {contractType?.value.startsWith("SELF_FINANCE") && (
        <CollapsibleCard id="self-finance-info" title="Self Finance Info" defaultOpen>
          {loading ? (
            <div className="animate-pulse flex flex-col gap-4">
              <div className="h-5 bg-foreground rounded-md"></div>
              <div className="h-5 bg-foreground rounded-md"></div>
            </div>
          ) : (
            <div className="divide-y divide-card/50">{renderRows(selfFinanceInfoRows)}</div>
          )}
        </CollapsibleCard>
      )}
      <CollapsibleCard
        id="invoice-info"
        title="Invoice / Load Info"
        partialDefaultCollapsed
        partialTargetId="client_invoice">
        {loading ? (
          <div className="animate-pulse flex flex-col gap-4">
            <div className="h-5 bg-foreground rounded-md"></div>
            <div className="h-5 bg-foreground rounded-md"></div>
          </div>
        ) : (
          <div className="divide-y divide-card/50">
            {renderRows(invoiceInfoRows)}
            <CollapsibleCard.Part id="client_invoice" defaultCollapsed={false}>
              {renderRows(invoiceInfoCollapsibleRows)}
            </CollapsibleCard.Part>
          </div>
        )}
      </CollapsibleCard>
      {isAssignedLoadsPage ? (
        <CollapsibleCard id="debtor-details" title="Debtor Details" defaultOpen>
          {loading ? (
            <div className="animate-pulse flex flex-col gap-4">
              <div className="h-5 bg-foreground rounded-md"></div>
              <div className="h-5 bg-foreground rounded-md"></div>
            </div>
          ) : (
            <div className="divide-y divide-card/50">{renderRows(debtorAssignedLoadRows())}</div>
          )}
        </CollapsibleCard>
      ) : (
        <CollapsibleCard
          id="debtor-details"
          title="Debtor Details"
          partialDefaultCollapsed
          partialTargetId="debtor_noa_status">
          {loading ? (
            <div className="animate-pulse flex flex-col gap-4">
              <div className="h-5 bg-foreground rounded-md"></div>
              <div className="h-5 bg-foreground rounded-md"></div>
            </div>
          ) : (
            <div className="divide-y divide-card/50">
              {renderRows(debtorDetailsRows)}
              <CollapsibleCard.Part id="debtor_noa_status" defaultCollapsed>
                {renderRows(debtorDetailsCollapsibleRows)}
              </CollapsibleCard.Part>
            </div>
          )}
        </CollapsibleCard>
      )}

      {!isCarrier && (
        <CollapsibleCard
          id="carrier-details"
          title="Carrier Details"
          partialTargetId="carrier_extended_info"
          partialDefaultCollapsed>
          {loading ? (
            <div className="animate-pulse flex flex-col gap-4">
              <div className="h-5 bg-foreground rounded-md"></div>
              <div className="h-5 bg-foreground rounded-md"></div>
            </div>
          ) : contractType?.value === "NON_FACTORED_BILL_OUT" ? (
            <p className="text-sm text-tertiary">
              No carrier is paid on non-factored bill out transactions
            </p>
          ) : (
            <div className="divide-y divide-card/50">
              {renderRows(carrierDetailsRows)}
              <CollapsibleCard.Part id="carrier_extended_info" defaultCollapsed>
                {renderRows(carrierDetailsCollapsibleRows)}
              </CollapsibleCard.Part>
            </div>
          )}
        </CollapsibleCard>
      )}
      {isAssignedLoadsPage ? (
        <CollapsibleCard title="YOUR PAY" id="carrier-pay" defaultOpen>
          {loading ? (
            <div className="animate-pulse flex flex-col gap-4">
              <div className="h-5 bg-foreground rounded-md"></div>
              <div className="h-5 bg-foreground rounded-md"></div>
            </div>
          ) : (
            <div className="divide-y divide-card/50">{renderRows(assignedLoadRows)}</div>
          )}
        </CollapsibleCard>
      ) : (
        <CollapsibleCard
          title={isCarrier ? "YOUR PAY" : "BROKER PAY"}
          id={isCarrier ? "carrier-pay" : "broker-pay"}
          {...(!isCarrier
            ? { partialTargetId: "your-fee", partialDefaultCollapsed: true }
            : { defaultOpen: true })}>
          {loading ? (
            <div className="animate-pulse flex flex-col gap-4">
              <div className="h-5 bg-foreground rounded-md"></div>
              <div className="h-5 bg-foreground rounded-md"></div>
            </div>
          ) : (
            <div className="divide-y divide-card/50">
              {renderRows(brokerPayRows)}
              <CollapsibleCard.Part id="your-fee" defaultCollapsed={false}>
                {renderRows(brokerPayCollapsibleRows)}
              </CollapsibleCard.Part>
            </div>
          )}
        </CollapsibleCard>
      )}
    </aside>
  );
}
